import { expandProfile, contractProfile } from './animations'
/**
 * Desktop UI for profile list/bios. Behaviour:
 * - Image grid displayed left. Profiles expand over top of image grid to fill the area when expanded.
 * - If profile larger than image grid area, whole block expands to fit it on open (and contracts when closed).
 * - List items displayed right. Include name and role.
 * - Profile includes full image, close button, name, LinkedIn icon/link and bio.
 * - Click on either image or list item tile to expand full profile. Identical data attributes used to link image grid/list versions.
 * - Rollover high
 * - Profile closes on close button click or when another profile opened.
 *
 * See Figma file for intended layout/behaviour:
 * https://www.figma.com/file/YQ73BIauH99X8ksuCY1hcZ/Marketing---website-content-blueprint?type=design&node-id=799-12598&mode=dev
 */
const DesktopProfile = (function () {
  let peopleBlocks = {}
  let isOpeningProfile = false

  /**
   * Initialise any event handlers required to interact with people image grid, list item tiles and profiles
   */
  const initEventHandlers = () => {
    // Each 'department' grouping represents a separate block on the page
    peopleBlocks.forEach((block) => {
      const imageTiles = block.querySelectorAll('.js-profile-image-tile')
      const listTiles = block.querySelectorAll('.js-desktop-list-tile')

      imageTiles.forEach((tile) => {
        tile.addEventListener('click', handleImageTileClick)
        tile.addEventListener('mouseenter', handleMouseEnterImageTile)
        tile.addEventListener('focus', handleMouseEnterImageTile)
        tile.addEventListener('mouseleave', handleMouseLeaveImageTile)
        tile.addEventListener('blur', handleMouseLeaveImageTile)

        const closeBtn = tile.parentNode.querySelector('.js-close-profile-btn')
        if (closeBtn) {
          closeBtn.addEventListener('click', handleCloseBtnClick)
        }
      })

      listTiles.forEach((tile) => {
        const tileLink = tile.querySelector('.js-profile-link')
        tile.addEventListener('click', handleListTileClick)
        tile.addEventListener('mouseenter', handleMouseEnterListTile)
        tileLink.addEventListener('focus', handleMouseEnterListTile)
        tile.addEventListener('mouseleave', handleMouseLeaveListTile)
        tileLink.addEventListener('blur', handleMouseLeaveListTile)
      })
    })
  }

  /**
   * Remove all event listeners
   */
  const killEventHandlers = () => {
    peopleBlocks.forEach((block) => {
      const imageTiles = block.querySelectorAll('.js-profile-image-tile')
      const listTiles = block.querySelectorAll('.js-desktop-list-tile')

      imageTiles.forEach((tile) => {
        tile.removeEventListener('click', handleImageTileClick)
        tile.removeEventListener('mouseenter', handleMouseEnterImageTile)
        tile.removeEventListener('focus', handleMouseEnterImageTile)
        tile.removeEventListener('mouseleave', handleMouseLeaveImageTile)
        tile.removeEventListener('blur', handleMouseLeaveImageTile)

        const closeBtn = tile.parentNode.querySelector('.js-close-profile-btn')
        if (closeBtn) {
          closeBtn.removeEventListener('click', handleCloseBtnClick)
        }
      })

      listTiles.forEach((tile) => {
        const tileLink = tile.querySelector('.js-profile-link')
        tile.removeEventListener('click', handleListTileClick)
        tile.removeEventListener('mouseenter', handleMouseEnterListTile)
        tileLink.removeEventListener('focus', handleMouseEnterListTile)
        tile.removeEventListener('mouseleave', handleMouseLeaveListTile)
        tileLink.removeEventListener('blur', handleMouseLeaveListTile)
      })
    })
  }

  /**
   * Handle click on image grid item
   * @param {Event} e
   */
  const handleImageTileClick = (e) => {
    e.preventDefault()

    // Allow cmd + click behaviour to open profile in new tab
    if (e.ctrlKey || e.metaKey) {
      window.open(e.currentTarget.getAttribute('href'), '_blank')
    } else {
      const linkEl = e.currentTarget
      const peopleFeatureParent = linkEl.closest('.js-people-feature')

      // Get corresponding list item for person
      const relatedListEl = peopleFeatureParent.querySelector(
        `.js-desktop-list-tile[data-person="${linkEl.dataset.person}"]`,
      )
      toggleProfile(linkEl, relatedListEl)
    }
  }

  /**
   * Handle click on list item tile
   * @param {Event} e
   */
  const handleListTileClick = (e) => {
    e.preventDefault()

    // Allow cmd + click behaviour to open profile in new tab
    if (e.ctrlKey || e.metaKey) {
      const itemAnchorLinkEl = e.target.closest('.js-mobile-profile-tile').querySelector('.js-profile-link')
      if (itemAnchorLinkEl) {
        window.open(itemAnchorLinkEl.getAttribute('href'), '_blank')
      }
    } else {
      const linkEl = e.currentTarget
      const peopleFeatureParent = linkEl.closest('.js-people-feature')

      // Get corresponding image grid item for person
      const relatedImageEl = peopleFeatureParent.querySelector(
        `.js-profile-image-tile[data-person=${linkEl.dataset.person}]`,
      )

      toggleProfile(relatedImageEl, linkEl)
    }
  }

  /**
   * Handle click on close button within profile (triggers click on tile element to toggle profile off)
   * @param {Event} e
   */
  const handleCloseBtnClick = (e) => {
    e.stopPropagation()
    const profile = e.currentTarget.closest('.c-profile')
    const imageTile = profile.parentNode.querySelector('.js-profile-image-tile')
    imageTile.click()
  }

  /**
   * Highlight corresponding list and image grid elements when list tile is hovered over
   * @param {Event} e
   */
  const handleMouseEnterListTile = (e) => {
    // Get tile element
    let listTileEl = e.currentTarget
    if (listTileEl.classList.contains('js-profile-link')) {
      listTileEl = e.currentTarget.closest('.js-desktop-list-tile')
    }

    // Get corresponding image grid item for person
    const peopleFeatureParent = listTileEl.closest('.js-people-feature')
    const imageTileEl = peopleFeatureParent.querySelector(
      `.js-profile-image-tile[data-person=${listTileEl.dataset.person}]`,
    )

    highlightHoverItem(listTileEl, imageTileEl, peopleFeatureParent)
  }

  /**
   * Reset hover state when mouse leaves highlighted list tile item
   * @param {Event} e
   */
  const handleMouseLeaveListTile = (e) => {
    let listTileEl = e.currentTarget
    if (listTileEl.classList.contains('js-profile-link')) {
      listTileEl = e.currentTarget.closest('.js-desktop-list-tile')
    }
    const peopleFeatureParent = listTileEl.closest('.js-people-feature')
    const imageTileEl = peopleFeatureParent.querySelector(
      `.js-profile-image-tile[data-person=${listTileEl.dataset.person}]`,
    )
    resetHoverItem(listTileEl, imageTileEl, peopleFeatureParent)
  }

  /**
   * Highlight corresponding list and image grid elements when image tile is hovered over
   * @param {Event} e
   */
  const handleMouseEnterImageTile = (e) => {
    const imageTileEl = e.currentTarget
    const peopleFeatureParent = imageTileEl.closest('.js-people-feature')
    const listTileEl = peopleFeatureParent.querySelector(
      `.js-desktop-list-tile[data-person=${imageTileEl.dataset.person}]`,
    )
    highlightHoverItem(listTileEl, imageTileEl, peopleFeatureParent)
  }

  /**
   * Reset hover state when mouse leaves highlighted image tile
   * @param {Event} e
   */
  const handleMouseLeaveImageTile = (e) => {
    const imageTileEl = e.currentTarget
    const peopleFeatureParent = imageTileEl.closest('.js-people-feature')
    const listTileEl = peopleFeatureParent.querySelector(
      `.js-desktop-list-tile[data-person=${imageTileEl.dataset.person}]`,
    )
    resetHoverItem(listTileEl, imageTileEl, peopleFeatureParent)
  }

  /**
   * Given the corresponding list/image tiles, highlight both.
   * @param {Element} listTile
   * @param {Element} imageTile
   * @param {Element} peopleFeatureParent
   */
  const highlightHoverItem = (listTile, imageTile, peopleFeatureParent) => {
    if (listTile && imageTile && peopleFeatureParent) {
      const imageGridParent = peopleFeatureParent.querySelector('.c-image-grid')
      listTile.classList.add('c-profile-list__tile--hover')
      imageTile.classList.add('c-image-grid__image-link--hover')
      imageGridParent.classList.add('c-image-grid--highlight-active')
    }
  }

  /**
   * Given the corresponding list/image tiles, reset hover state.
   * @param {Element} listTile
   * @param {Element} imageTile
   * @param {Element} peopleFeatureParent
   */
  const resetHoverItem = (listTile, imageTile, peopleFeatureParent) => {
    if (listTile && imageTile && peopleFeatureParent) {
      const imageGridParent = peopleFeatureParent.querySelector('.c-image-grid')
      listTile.classList.remove('c-profile-list__tile--hover')
      imageTile.classList.remove('c-image-grid__image-link--hover')
      imageGridParent.classList.remove('c-image-grid--highlight-active')
    }
  }

  /**
   * Given corresponding list and image tile elements, toggle profile open or closed (based on current state)
   * and update affected UI state accordingly.
   * @param {Element} imageTileEl
   * @param {Element} listTileEl
   */
  const toggleProfile = async (imageTileEl, listTileEl) => {
    if (imageTileEl && listTileEl) {
      const profile = imageTileEl.parentNode.querySelector('.c-profile')
      const gridParent = imageTileEl.closest('.js-profile-image-grid')

      // If already open, close element
      if (listTileEl.classList.contains('c-profile-list__tile--active')) {
        if (!isOpeningProfile) {
          if (gridParent) {
            gridParent.classList.remove('c-image-grid--profile-open')
          }
        } else {
          isOpeningProfile = false
        }

        listTileEl.classList.remove('c-profile-list__tile--active')

        // Remove anchor from URL on close
        history.replaceState('', document.title, window.location.pathname + window.location.search)

        // Update aria attributes
        if (profile) {
          imageTileEl.setAttribute('aria-expanded', 'false')
          profile.querySelectorAll('.js-aria-controls').forEach((item) => {
            item.setAttribute('aria-expanded', 'false')
          })
          listTileEl.querySelectorAll('.js-aria-controls').forEach((item) => {
            item.setAttribute('aria-expanded', 'false')
          })

          // Animate profile closed
          const result = await contractProfile(imageTileEl.parentNode)
          // After the contractProfile animation completes,
          // set focus and scroll to the image tile that was used to open the profile.
          // This allows users to continue interacting with the list from their previous position.
          if (result === 'height-animated') {
            imageTileEl.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            })
            imageTileEl.focus({ preventScroll: true })
          }
        }
      } else {
        const activeListItem = listTileEl
          .closest('.js-profile-tile-list')
          .querySelector('.c-profile-list__tile--active')

        // If another profile already open, close it.
        if (activeListItem) {
          isOpeningProfile = true
          listTileEl.closest('.js-people-feature').classList.add('js-expand-pending')
          activeListItem.click()
        } else {
          gridParent.classList.add('c-image-grid--profile-open')
        }

        listTileEl.classList.add('c-profile-list__tile--active')
        if (profile) {
          // Update aria attributes
          imageTileEl.setAttribute('aria-expanded', 'true')
          profile.querySelectorAll('.js-aria-controls').forEach((item) => {
            item.setAttribute('aria-expanded', 'true')
          })
          listTileEl.querySelectorAll('.js-aria-controls').forEach((item) => {
            item.setAttribute('aria-expanded', 'true')
          })
          // Animate profile open
          expandProfile(imageTileEl.parentNode)

          // Update URL with active anchor link
          const hashLink = imageTileEl.href.split('#')
          if (hashLink.length > 1) {
            history.replaceState(
              '',
              document.title,
              window.location.pathname + window.location.search + `#${hashLink[1]}`,
            )
          }

          // Move focus to close button in profile on open for keyboard users
          const closeBtn = profile.querySelector('.js-close-profile-btn')
          if (closeBtn) {
            closeBtn.focus({
              preventScroll: true,
            })
          }
        }
      }
    }
  }

  /**
   * Given identifier string for person, trigger click to open corresponding profile on load (if valid).
   * @param {string} profileIdentifier
   */
  const loadActiveProfile = (profileIdentifier) => {
    const listTileEl = document.querySelector(`.js-desktop-list-tile[data-person="${profileIdentifier}"]`)
    if (listTileEl) listTileEl.click()
  }

  return {
    init: (activeProfile) => {
      peopleBlocks = document.querySelectorAll('.js-people-feature')
      initEventHandlers()
      if (activeProfile) {
        loadActiveProfile(activeProfile)
      }

      // Update aria attributes to match desktop behaviour
      document.querySelectorAll('.js-profile-link').forEach((link) => {
        link.setAttribute('aria-controls', link.getAttribute('aria-controls').replace('-mobile', ''))
      })
    },
    kill: () => {
      killEventHandlers()
    },
  }
})()

export default DesktopProfile
